import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { ChevronDown, ChevronUp, Loader2, MessageCircle } from 'lucide-react';
import { useState, type JSX } from 'react';
import type { ReasoningFragment } from '@heidi/evidence/contracts';

interface InlineReasoningBlockProps {
  content: ReasoningFragment[];
  isStreaming?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

/**
 * Extract all bold headings from markdown text
 * Reused from ReasoningBlock/ActivityBox
 */
function extractHeadings(text: string): string[] {
  const regex = /\*\*(.*?)\*\*/g;
  const headings: string[] = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    const heading = match[1]?.trim();
    if (heading) {
      headings.push(heading);
    }
  }

  return headings;
}

/**
 * V2 Inline Reasoning Block - Collapsible thinking indicator for block-based timeline
 *
 * Visual states:
 * - STREAMING: Animated spinner with "Thinking..."
 * - COLLAPSED: Static icon with "Thinking..." and chevron
 * - EXPANDED: Shows extracted headings/reasoning steps
 *
 * NOTE: No duration display per user decision - just "Thinking..."
 */
export const InlineReasoningBlock = ({
  content,
  isStreaming = false,
  defaultExpanded = false,
  className,
}: InlineReasoningBlockProps): JSX.Element | null => {
  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Extract reasoning text and headings
  const reasoningText = content.map(([text]) => text).join('');
  const headings = extractHeadings(reasoningText);
  const hasContent = headings.length > 0;

  // Don't render if no content and not streaming
  if (!isStreaming && content.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-border duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-3 text-left hover:bg-surface-hover"
        disabled={!hasContent && !isStreaming}
      >
        <div className="flex items-center gap-2">
          {isStreaming ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-text-secondary" />
          ) : (
            <MessageCircle className="size-4 shrink-0 text-text-secondary" />
          )}
          <span className="text-sm text-text-secondary">{t('chat.thinking')}</span>
        </div>
        {hasContent &&
          (isExpanded ? (
            <ChevronUp className="size-4 shrink-0 text-text-tertiary" />
          ) : (
            <ChevronDown className="size-4 shrink-0 text-text-tertiary" />
          ))}
      </button>

      {/* Expanded content - only if we have headings */}
      {isExpanded && hasContent && (
        <div className="border-t border-border p-3">
          <ul className="space-y-1.5">
            {headings.map((heading, idx) => (
              <li
                key={`heading-${idx}`}
                className="flex min-w-0 items-start gap-2 text-sm text-text-secondary"
              >
                <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-text-tertiary" />
                <span className="min-w-0 break-words">{heading}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
